/**
 * Configuration — Notifications — events, recipients, methods, timing references, sample rules
 */

import {
  buildSampleLimitRules,
  formatLimitDisplay,
  getLimitForLabel,
  getUnitLabel,
  normalizeLimitForm,
} from './limitsThresholdsConfig';

export const NOTIFICATIONS_ROUTE = '/admin/configuration/notifications';

export const RECIPIENT_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'business', label: 'Business' },
  { value: 'team_member', label: 'Team Member' },
  { value: 'visitor', label: 'Visitor' },
];

export const SEND_TO_OPTIONS = [
  { value: 'affected_customer', label: 'Affected Customer', recipient: 'customer' },
  { value: 'affected_business', label: 'Affected Business', recipient: 'business' },
  { value: 'current_platform_owner', label: 'Current Platform Owner', recipient: 'team_member' },
  { value: 'support_team_member', label: 'Support Team Member', recipient: 'team_member' },
  { value: 'ticket_submitter', label: 'Ticket Submitter', recipient: 'visitor' },
];

export const METHOD_OPTIONS = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'email', label: 'Email' },
  { value: 'browser_push', label: 'Browser Push' },
];

export const TYPE_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'optional', label: 'Optional' },
];

export const TIMING_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'threshold_based', label: 'Threshold-Based' },
];

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals', multi: false },
];

/** Optional Condition catalogue — NOT DEFINED in full; keep empty until confirmed. */
export const CONDITION_FIELD_OPTIONS = [];

const DEFAULT_WORDING_LIMITS = {
  dashboardTitle: 80,
  dashboardMessage: 240,
  emailSubject: 120,
  emailBody: 2000,
  browserPushTitle: 65,
  shortMessage: 120,
};

const WORDING_BY_METHOD = {
  dashboard: [
    { key: 'dashboardTitle', label: 'Dashboard Title' },
    { key: 'dashboardMessage', label: 'Dashboard Message' },
  ],
  email: [
    { key: 'emailSubject', label: 'Email Subject' },
    { key: 'emailBody', label: 'Email Body' },
  ],
  browser_push: [
    { key: 'browserPushTitle', label: 'Browser Push Title' },
    { key: 'shortMessage', label: 'Short Message' },
  ],
};

function eventDef(partial) {
  return {
    available: true,
    complete: true,
    types: ['required'],
    timings: ['immediate'],
    timingContext: 'event_occurrence',
    conditionFields: [],
    placeholders: [],
    wordingLimits: DEFAULT_WORDING_LIMITS,
    destinationRequirement: 'optional',
    destinations: [
      { value: 'related_record', label: 'Related Record' },
      { value: 'account_home', label: 'Account Home' },
    ],
    ...partial,
  };
}

export const EVENT_OPTIONS = [
  eventDef({
    value: 'appeal_decision_issued',
    id: 'sample-evt-appeal-decision',
    label: 'Appeal Decision Issued',
    meaning: 'A decision has been recorded for an Appeal.',
    recipients: ['customer'],
    sendTo: { customer: ['affected_customer'] },
    methods: { customer: ['dashboard', 'email'] },
    types: ['required'],
    timings: ['immediate'],
    destinationRequirement: 'optional',
  }),
  eventDef({
    value: 'subscription_payment_failed',
    id: 'sample-evt-subscription-payment-failed',
    label: 'Subscription Payment Failed',
    meaning: 'A Business subscription payment failed. Email cannot be disabled through Business preferences.',
    recipients: ['business'],
    sendTo: { business: ['affected_business'] },
    methods: { business: ['email'] },
    types: ['required'],
    timings: ['immediate'],
    destinationRequirement: 'optional',
  }),
  eventDef({
    value: 'new_message_received',
    id: 'sample-evt-new-message-received',
    label: 'New Message Received',
    meaning: 'One newly received Message Thread. Its parent Message is not another received Thread. Confirmed New Customer Message example: Optional, Immediate Browser Push, No Conditions.',
    recipients: ['business'],
    sendTo: { business: ['affected_business'] },
    methods: { business: ['browser_push'] },
    types: ['optional'],
    timings: ['immediate'],
    conditionFields: [],
    destinationRequirement: 'optional',
  }),
  eventDef({
    value: 'payment_remains_outstanding',
    id: 'sample-evt-payment-remains-outstanding',
    label: 'Payment Remains Outstanding',
    meaning: 'Business billing reminder while payment remains outstanding. Email cannot be disabled through Business preferences.',
    recipients: ['business'],
    sendTo: { business: ['affected_business'] },
    methods: { business: ['email'] },
    types: ['required'],
    timings: ['threshold_based'],
    timingContext: 'outstanding_payment',
    destinationRequirement: 'optional',
  }),
  eventDef({
    value: 'integration_failure',
    id: 'sample-evt-integration-failure',
    label: 'Integration Failure',
    meaning: 'An approved service enters a verified Error/Failed incident. Repeated observations of the same unresolved incident are one occurrence.',
    recipients: ['team_member'],
    sendTo: { team_member: ['current_platform_owner'] },
    methods: { team_member: ['dashboard', 'email'] },
    types: ['required'],
    timings: ['immediate'],
    conditionFields: [],
    destinationRequirement: 'required',
    destinations: [{ value: 'integrations_service_context', label: 'Integrations service context' }],
  }),
  eventDef({
    value: 'integration_security_alert',
    id: 'sample-evt-integration-security-alert',
    label: 'Integration Security Alert',
    meaning: 'A confirmed integration-related security incident or security-sensitive access/ownership change.',
    recipients: ['team_member'],
    sendTo: { team_member: ['current_platform_owner'] },
    methods: { team_member: ['dashboard', 'email'] },
    types: ['required'],
    timings: ['immediate'],
    conditionFields: [],
    destinationRequirement: 'required',
    destinations: [{ value: 'integrations_service_context', label: 'Integrations service context' }],
  }),
];

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase().replace(/\s+/g, ' ') === String(b || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function getEventDefinition(value) {
  return EVENT_OPTIONS.find((option) => option.value === value) || null;
}

export function getEventLabel(value) {
  const event = getEventDefinition(value);
  if (!event) return value ? `${value} Unavailable` : '';
  return event.available === false ? `${event.label} Unavailable` : event.label;
}

export function getEventDisplay(value) {
  return getEventLabel(value) || '—';
}

export function getEventMeaning(value) {
  return getEventDefinition(value)?.meaning || '';
}

export function getEventIdentity(value) {
  const event = getEventDefinition(value);
  if (!event) return value ? String(value) : '';
  return event.id || '';
}

export function getRecipientLabel(value) {
  return labelFromOptions(RECIPIENT_OPTIONS, value);
}

export function getSendToLabel(value) {
  const mapped = value === 'assigned_team_member' ? 'support_team_member' : value;
  return labelFromOptions(SEND_TO_OPTIONS, mapped);
}

export function getMethodLabel(value) {
  return labelFromOptions(METHOD_OPTIONS, value);
}

export function getTypeLabel(value) {
  return labelFromOptions(TYPE_OPTIONS, value);
}

export function getTimingLabel(value) {
  return labelFromOptions(TIMING_OPTIONS, value);
}

export function getRecipientOptions(event) {
  const definition = getEventDefinition(event);
  if (!definition) return [];
  return RECIPIENT_OPTIONS.filter((option) => (definition.recipients || []).includes(option.value));
}

export function getSendToOptions(event, recipient) {
  const definition = getEventDefinition(event);
  if (!definition || !recipient) return [];
  const allowed = definition.sendTo?.[recipient] || [];
  return SEND_TO_OPTIONS.filter((option) => option.recipient === recipient && allowed.includes(option.value));
}

export function getMethodOptions(event, recipient) {
  const definition = getEventDefinition(event);
  if (!definition || !recipient) return [];
  const allowed = definition.methods?.[recipient] || definition.methods?.all || [];
  return METHOD_OPTIONS.filter((option) => allowed.includes(option.value));
}

export function getTypeOptions(event) {
  const definition = getEventDefinition(event);
  if (!definition) return [];
  return TYPE_OPTIONS.filter((option) => (definition.types || []).includes(option.value));
}

export function getTimingOptions(event) {
  const definition = getEventDefinition(event);
  if (!definition) return [];
  return TIMING_OPTIONS.filter((option) => (definition.timings || []).includes(option.value));
}

export function getDestinationRequirement(event) {
  const definition = getEventDefinition(event);
  if (!definition) return '';
  return definition.destinationRequirement || '';
}

export function getDestinationOptions(event) {
  const definition = getEventDefinition(event);
  if (!definition || definition.destinationRequirement === 'not_available') return [];
  return definition.destinations || [];
}

export function getDestinationLabel(event, value) {
  return labelFromOptions(getDestinationOptions(event), value);
}

export function getWordingFields(method) {
  return WORDING_BY_METHOD[method] || [];
}

export function getWordingLimit(event, field) {
  const definition = getEventDefinition(event);
  if (!definition || definition.wordingLimits == null) return null;
  return definition.wordingLimits[field] ?? null;
}

export function getAvailableConditionFields(event) {
  const fields = getEventDefinition(event)?.conditionFields || [];
  return CONDITION_FIELD_OPTIONS.filter((option) => fields.includes(option.value));
}

export function getConditionFieldLabel(value) {
  return labelFromOptions(CONDITION_FIELD_OPTIONS, value);
}

export function getConditionOperatorLabel(value) {
  return labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);
}

export function getConditionOperators(field) {
  const definition = CONDITION_FIELD_OPTIONS.find((option) => option.value === field);
  if (!definition) return [];
  return CONDITION_OPERATOR_OPTIONS.filter((option) => definition.operators.includes(option.value));
}

export function getConditionValueOptions(field) {
  return CONDITION_FIELD_OPTIONS.find((option) => option.value === field)?.values || [];
}

export function getConditionValueLabel(field, value) {
  return labelFromOptions(getConditionValueOptions(field), value) || String(value || '');
}

export function operatorIsMulti(operator) {
  return CONDITION_OPERATOR_OPTIONS.find((option) => option.value === operator)?.multi === true;
}

export function getLimitRules(limitRules) {
  return Array.isArray(limitRules) && limitRules.length ? limitRules.map(normalizeLimitForm) : buildSampleLimitRules();
}

export function getTimingReferenceOptions(purpose, limitRules, currentId) {
  return getLimitRules(limitRules)
    .filter((rule) => {
      if (rule.limitFor !== purpose || rule.limitType !== 'single_value') return false;
      if (rule.id === currentId) return true;
      return true;
    })
    .map((rule) => ({
      value: rule.id,
      label: `${rule.id} — ${rule.ruleName}`,
      availability: 'active',
      displayValue: `${formatLimitDisplay(rule)} ${getUnitLabel(rule.unit)}`.trim(),
      paused: false,
    }));
}

export function getTimingReference(ruleId, limitRules) {
  return getLimitRules(limitRules).find((rule) => rule.id === ruleId) || null;
}

export function formatTimingReferenceDetail(ruleId, limitRules) {
  const rule = getTimingReference(ruleId, limitRules);
  if (!rule) return { label: ruleId || '—', value: '—', availability: 'Unavailable' };
  const unit = getUnitLabel(rule.unit);
  const value = rule.operator === 'equal_to'
    ? `${rule.value} ${unit}`.trim()
    : `${formatLimitDisplay(rule)} ${unit}`.trim();
  return {
    label: `${rule.id} — ${rule.ruleName}`,
    value,
    availability: 'Active',
    paused: false,
    purpose: getLimitForLabel(rule.limitFor),
    ruleState: 'Active',
  };
}

export function isTimingReferenceSelectable(rule, purpose) {
  if (!rule) return false;
  return rule.limitFor === purpose && rule.limitType === 'single_value';
}

export function getReferencedMaximumSends(ruleId, limitRules) {
  const rule = getTimingReference(ruleId, limitRules);
  const number = Number(rule?.value);
  return Number.isFinite(number) ? number : null;
}

export function needsRepeatInterval(maximumSendsId, limitRules) {
  const number = getReferencedMaximumSends(maximumSendsId, limitRules);
  return number != null && number > 1;
}

export function nextNotificationRuleId(allRules) {
  const nums = (allRules || []).map((rule) => {
    const match = String(rule.id || '').match(/^(?:NRL|NTF)-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `NRL-${String(max + 1).padStart(4, '0')}`;
}

export function createEmptyCondition() {
  return { id: `cond-${Date.now()}`, field: '', operator: '', values: [] };
}

export function createEmptyNotificationForm() {
  return {
    id: '',
    ruleName: '',
    event: '',
    recipient: '',
    sendTo: '',
    method: '',
    type: '',
    timing: '',
    initialDelay: '',
    maximumSends: '',
    repeatInterval: '',
    dashboardTitle: '',
    dashboardMessage: '',
    emailSubject: '',
    emailBody: '',
    browserPushTitle: '',
    shortMessage: '',
    destinationLink: '',
    conditions: [],
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function normalizeCondition(raw) {
  const values = Array.isArray(raw?.values)
    ? raw.values.filter(Boolean)
    : raw?.value
      ? [raw.value]
      : [];
  return {
    id: raw?.id || `cond-${Math.random().toString(36).slice(2, 8)}`,
    field: raw?.field || '',
    operator: raw?.operator || '',
    values,
  };
}

export function applyNotificationDerivedFields(form) {
  const next = { ...form };
  const types = getTypeOptions(next.event);
  if (types.length === 1) next.type = types[0].value;
  const timings = getTimingOptions(next.event);
  if (timings.length === 1) next.timing = timings[0].value;
  if (next.timing !== 'threshold_based') {
    next.initialDelay = '';
    next.maximumSends = '';
    next.repeatInterval = '';
  }
  if (getDestinationRequirement(next.event) === 'not_available') next.destinationLink = '';
  const wordingKeys = new Set(getWordingFields(next.method).map((field) => field.key));
  Object.keys(WORDING_BY_METHOD).forEach((method) => {
    WORDING_BY_METHOD[method].forEach((field) => {
      if (!wordingKeys.has(field.key)) next[field.key] = '';
    });
  });
  return next;
}

function optionValues(options) {
  return (options || []).map((option) => option.value);
}

export function applyNotificationSelectionChange(form, field, value) {
  let next = { ...form, [field]: value };
  if (field === 'event') {
    if (!optionValues(getRecipientOptions(next.event)).includes(next.recipient)) next.recipient = '';
    if (!optionValues(getSendToOptions(next.event, next.recipient)).includes(next.sendTo)) next.sendTo = '';
    if (!optionValues(getMethodOptions(next.event, next.recipient)).includes(next.method)) next.method = '';
    if (!optionValues(getTypeOptions(next.event)).includes(next.type)) next.type = '';
    if (!optionValues(getTimingOptions(next.event)).includes(next.timing)) next.timing = '';
  }
  if (field === 'recipient') {
    if (!optionValues(getSendToOptions(next.event, next.recipient)).includes(next.sendTo)) next.sendTo = '';
    if (!optionValues(getMethodOptions(next.event, next.recipient)).includes(next.method)) next.method = '';
  }
  if (field === 'timing' && next.timing !== 'threshold_based') {
    next.initialDelay = '';
    next.maximumSends = '';
    next.repeatInterval = '';
  }
  if (field === 'maximumSends' && !needsRepeatInterval(next.maximumSends)) {
    next.repeatInterval = '';
  }
  return applyNotificationDerivedFields(next);
}

export function normalizeNotificationForm(raw = {}) {
  const event = raw.event || raw.triggerEvent || '';
  const recipient = raw.recipient || raw.recipientType || '';
  let sendTo = raw.sendTo || raw.recipientResolution || '';
  if (sendTo === 'assigned_team_member') sendTo = 'support_team_member';
  const method = raw.method || (raw.channel === 'in_platform' ? 'dashboard' : raw.channel) || '';
  const timing = raw.timing || (raw.sendTiming === 'on_event' ? 'immediate' : raw.sendTiming ? 'threshold_based' : '');
  const derived = applyNotificationDerivedFields({
    ...createEmptyNotificationForm(),
    ...raw,
    event,
    recipient,
    sendTo,
    method: method === 'sms' ? '' : method,
    type: raw.type || (raw.preferenceRule === 'required_service' ? 'required' : raw.preferenceRule === 'respect_preferences' ? 'optional' : ''),
    timing,
    initialDelay: raw.initialDelay || '',
    maximumSends: raw.maximumSends || '',
    repeatInterval: raw.repeatInterval || '',
    dashboardTitle: raw.dashboardTitle || '',
    dashboardMessage: raw.dashboardMessage || '',
    emailSubject: raw.emailSubject || '',
    emailBody: raw.emailBody || '',
    browserPushTitle: raw.browserPushTitle || '',
    shortMessage: raw.shortMessage || '',
    destinationLink: raw.destinationLink || '',
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : [],
  });
  return {
    ...createEmptyNotificationForm(),
    ...raw,
    ...derived,
    ruleName: raw.ruleName || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

export function conditionValues(condition) {
  return [...new Set((condition?.values || []).map((value) => String(value || '').trim()).filter(Boolean))];
}

export function conditionFingerprint(condition) {
  return [condition.field || '', condition.operator || '', conditionValues(condition).slice().sort().join(',')].join('|');
}

export function equivalentConditions(a = [], b = []) {
  const left = (a || []).map(conditionFingerprint).sort().join('||');
  const right = (b || []).map(conditionFingerprint).sort().join('||');
  return left === right;
}

export function notificationScopeKey(rule) {
  const form = normalizeNotificationForm(rule);
  // Coverage: Event + recipient relationship + Method (+ Conditions compared separately).
  // Timing / wording / labels do not separate coverage.
  return [form.event, form.sendTo, form.method].join('|');
}

export function wordingFingerprint(rule) {
  const form = normalizeNotificationForm(rule);
  return getWordingFields(form.method).map((field) => String(form[field.key] || '').trim()).join('|');
}

export function settingsFingerprint(rule) {
  const form = normalizeNotificationForm(rule);
  return [
    form.type,
    form.timing,
    form.destinationLink || '',
    form.initialDelay || '',
    form.maximumSends || '',
    form.repeatInterval || '',
    wordingFingerprint(form),
  ].join('|');
}

export function conditionsMutuallyExclusive(a = [], b = []) {
  if (!(a || []).length || !(b || []).length) return false;
  const fields = [...new Set([...(a || []), ...(b || [])].map((condition) => condition.field).filter(Boolean))];
  return fields.some((field) => {
    const left = (a || []).filter((condition) => condition.field === field).flatMap(conditionValues);
    const right = (b || []).filter((condition) => condition.field === field).flatMap(conditionValues);
    return left.length && right.length && left.every((value) => !right.includes(value));
  });
}

export function coverageOverlaps(a, b) {
  if (notificationScopeKey(a) !== notificationScopeKey(b)) return false;
  if (!(a.conditions || []).length || !(b.conditions || []).length) return true;
  if (conditionsMutuallyExclusive(a.conditions, b.conditions)) return false;
  return true;
}

export function findDuplicateNotificationRule(rules, candidate, excludeId) {
  const form = normalizeNotificationForm(candidate);
  const settings = settingsFingerprint(form);
  return (rules || []).map(normalizeNotificationForm).find((rule) => (
    rule.id !== excludeId
    && notificationScopeKey(rule) === notificationScopeKey(form)
    && equivalentConditions(rule.conditions, form.conditions)
    && settingsFingerprint(rule) === settings
  )) || null;
}

export function findConflictingNotificationRule(rules, candidate, excludeId) {
  const form = normalizeNotificationForm(candidate);
  const settings = settingsFingerprint(form);
  return (rules || []).map(normalizeNotificationForm).find((rule) => (
    rule.id !== excludeId
    && notificationScopeKey(rule) === notificationScopeKey(form)
    && equivalentConditions(rule.conditions, form.conditions)
    && settingsFingerprint(rule) !== settings
  )) || null;
}

export function findOverlappingNotificationRules(rules, candidate, excludeId) {
  const form = normalizeNotificationForm(candidate);
  return (rules || []).map(normalizeNotificationForm).filter((rule) => (
    rule.id !== excludeId && coverageOverlaps(rule, form)
  ));
}

export function formatConditionLine(condition) {
  const field = getConditionFieldLabel(condition.field);
  const operator = getConditionOperatorLabel(condition.operator);
  const values = (condition.values || []).map((value) => getConditionValueLabel(condition.field, value));
  return [field, operator, values.join(', ')].filter(Boolean).join(' · ');
}

export function formatConditionsPreview(conditions = []) {
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map(formatConditionLine);
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatTimingColumn(rule, limitRules) {
  const form = normalizeNotificationForm(rule);
  const label = getTimingLabel(form.timing) || '—';
  if (form.timing !== 'threshold_based') return label;
  const required = ['initialDelay', 'maximumSends'];
  if (needsRepeatInterval(form.maximumSends, limitRules)) required.push('repeatInterval');
  const unavailable = required.some((field) => {
    const detail = formatTimingReferenceDetail(form[field], limitRules);
    return !form[field] || detail.paused || detail.availability === 'Unavailable';
  });
  return unavailable ? `${label} · Unavailable` : label;
}

export function formatNotificationDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  const hours24 = date.getUTCHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const suffix = hours24 >= 12 ? 'pm' : 'am';
  return `${day} ${month} ${year}, ${hours12}:${minutes} ${suffix}`;
}

export function sortNotificationRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

function actorFields(stamp) {
  return {
    addedBy: 'Sample Admin',
    lastUpdatedBy: 'Sample Admin',
    dateAdded: stamp,
    lastUpdated: stamp,
  };
}

export function buildSampleNotificationRules() {
  return [
    {
      id: 'NRL-0005',
      ruleName: 'Outstanding Payment Reminder',
      event: 'payment_remains_outstanding',
      recipient: 'business',
      sendTo: 'affected_business',
      method: 'email',
      type: 'required',
      timing: 'threshold_based',
      initialDelay: 'LTH-0007',
      maximumSends: 'LTH-0009',
      repeatInterval: 'LTH-0008',
      emailSubject: 'Outstanding payment.',
      emailBody: 'A payment remains outstanding on your account.',
      destinationLink: '',
      conditions: [],
      ...actorFields('2026-08-16T10:35:00.000Z'),
    },
    {
      id: 'NRL-0004',
      ruleName: 'New Customer Message',
      event: 'new_message_received',
      recipient: 'business',
      sendTo: 'affected_business',
      method: 'browser_push',
      type: 'optional',
      timing: 'immediate',
      browserPushTitle: 'New customer message.',
      shortMessage: 'A new customer message is available.',
      destinationLink: '',
      conditions: [],
      ...actorFields('2026-08-16T10:20:00.000Z'),
    },
    {
      id: 'NRL-0003',
      ruleName: 'Failed Payment',
      event: 'subscription_payment_failed',
      recipient: 'business',
      sendTo: 'affected_business',
      method: 'email',
      type: 'required',
      timing: 'immediate',
      emailSubject: 'Failed payment.',
      emailBody: 'A subscription payment could not be completed.',
      destinationLink: '',
      conditions: [],
      ...actorFields('2026-08-16T09:35:00.000Z'),
    },
    {
      id: 'NRL-0002',
      ruleName: 'Appeal Decision Email',
      event: 'appeal_decision_issued',
      recipient: 'customer',
      sendTo: 'affected_customer',
      method: 'email',
      type: 'required',
      timing: 'immediate',
      emailSubject: 'Appeal decision available.',
      emailBody: 'A decision has been recorded for your appeal.',
      destinationLink: '',
      conditions: [],
      ...actorFields('2026-08-16T09:18:00.000Z'),
    },
    {
      id: 'NRL-0001',
      ruleName: 'Appeal Decision Dashboard',
      event: 'appeal_decision_issued',
      recipient: 'customer',
      sendTo: 'affected_customer',
      method: 'dashboard',
      type: 'required',
      timing: 'immediate',
      dashboardTitle: 'Appeal decision available.',
      dashboardMessage: 'A decision has been recorded for your appeal.',
      destinationLink: '',
      conditions: [],
      ...actorFields('2026-08-16T09:10:00.000Z'),
    },
  ].map((rule) => applyNotificationDerivedFields(normalizeNotificationForm(rule)));
}

/** @deprecated aliases kept for older simulation imports */
export const NOTIFICATION_MODULE_OPTIONS = RECIPIENT_OPTIONS;
export const TRIGGER_TYPE_OPTIONS = [];
export const RECIPIENT_TYPE_OPTIONS = RECIPIENT_OPTIONS;
export const CHANNEL_OPTIONS = METHOD_OPTIONS;
export const SEND_TIMING_OPTIONS = TIMING_OPTIONS;
export const SUPPRESSION_OPTIONS = [];
export const PREFERENCE_RULE_OPTIONS = TYPE_OPTIONS;
export const TRIGGER_EVENT_OPTIONS = EVENT_OPTIONS.map(({ value, label }) => ({ value, label }));

export function getNotificationModuleLabel(value) {
  return getRecipientLabel(value);
}

export function getRecipientTypeLabel(value) {
  return getRecipientLabel(value);
}

export function getRecipientResolutionLabel(_recipient, value) {
  return getSendToLabel(value);
}

export function normalizeNotificationStatus() {
  return '';
}

export function formatSuppressionColumn() {
  return '—';
}
